import { initialCommentsState, CommentsState, PostsCommentsState } from 'mk2/apps/blogs/containers/Comments/Comments.reducers';
import {
    getCommentIdsOrNull,
    getPostsState,
} from 'mk2/apps/blogs/containers/Comments/Comments.selectors';
import { getPostModel } from 'mk2/apps/blogs/helpers';
import { State } from 'mk2/apps/groups/containers/GroupShared/GroupShared.reducers';
import { getComplaintsComplaintsCounts } from 'mk2/containers/Complaints/Complaints.selectors';
import { memoizeLRU, memoizeOne } from 'mk2/helpers/cache';
import { AppState } from 'mk2/reducers';
import { EntitiesState } from 'mk2/reducers/entities';
import { LoadingState } from 'mk2/reducers/loadingState';
import {
    BlogFeedItemEntity,
    BlogPostEntity,
    BlogPostSchema,
    GroupMemberEntity,
    GroupMemberRoles,
    GroupMemberSchema,
    ImageEntity,
    ImageSchema,
    UsersByGroupId,
} from 'mk2/schemas';
import { findDenormalizedEntitiesByCond } from 'mk2/selectors';
import { createSelector } from 'reselect';

export const getGroupSharedState = (state: AppState): State =>
    state.containers.groups.groupShared;

export const findGroupSharedModerators = (state: AppState, groupId: number): GroupMemberEntity[] =>
    findDenormalizedEntitiesByCond<GroupMemberEntity>(
        state,
        GroupMemberSchema,
        `cacheKey-${findGroupSharedModerators.name}-${groupId}`,
        (normalizedGroupMember: any) => (
            normalizedGroupMember.group === groupId &&
            normalizedGroupMember.role === GroupMemberRoles.MODERATOR
        ),
    );

const getPostsCommentsCountCached = memoizeOne(
    (posts: BlogPostEntity[], postsCommentsState: PostsCommentsState): {[postId: number]: number} =>
        posts.reduce((acc, post) => {
            const postKey = `${getPostModel(post)}/${post.id}`;
            const postCommentsState: CommentsState =  postsCommentsState[postKey] || initialCommentsState;
            // BUG: postsCommentsState.ids.length is count of all fetched comments, but commentsCount is count of not deleted comments
            acc[post.id] =
                (postCommentsState.loadingState === LoadingState.SUCCESS ||
                 postCommentsState.loadingState === LoadingState.SUCCESS_ON_SERVER)
                    ? postCommentsState.ids.length
                    : post.commentsCount;

            return acc;
        }, {}));


export const getPostsFromFeedItemsCached = memoizeLRU(1,
    (feedItems: BlogFeedItemEntity[]): BlogPostEntity[] => {
        const feedItemsPosts: BlogPostEntity[] = [];
        for (const feedItem of feedItems) {
            if (feedItem.post) {
                feedItemsPosts.push(feedItem.post);
            }
        }
        return feedItemsPosts;
    },
);

export const getPostsCommentsCount = (state: AppState, posts: BlogPostEntity[]): {[postId: number]: number} =>
    getPostsCommentsCountCached(posts, getPostsState(state));

export const getCommentComplaintsCounts = createSelector(
    [
        (state: AppState) => state.entities,
        (state: AppState) => state.containers.blogs.shared.comments,
        (state: AppState, postIds: number[]) => postIds,
        (state: AppState, postIds: number[], commentsLoadingState: LoadingState) => commentsLoadingState,
    ],
    (entities: EntitiesState, commentsState: PostsCommentsState, postIds: number[], commentsLoadingState: LoadingState) => {
        const blogPostEntities = entities[BlogPostSchema.key];
        if (!blogPostEntities) {
            return {};
        }
        return postIds.reduce((acc, postId) => {
            const key = `message/${postId}`;
            const commentIds = getCommentIdsOrNull(commentsState, key);

            acc[postId] = (commentIds !== null && commentsLoadingState !== LoadingState.LOADING)
                ? getComplaintsComplaintsCounts(entities, 'photoblog-comment', commentIds)
                : (blogPostEntities[postId]
                    ? (blogPostEntities[postId] as BlogPostEntity).complaintsCount  // note: not a fully denormalized entity
                    : 0
                );

            return acc;
        }, {});
    },
);

export const getImageCommentComplaintsCounts = createSelector(
    [
        (state: AppState) => state.entities,
        (state: AppState) => state.containers.blogs.shared.comments,
        (state: AppState, imageIds: number[]) => imageIds,
        (state: AppState, imageIds: number[], commentsLoadingState: LoadingState) => commentsLoadingState,
    ],
    (entities: EntitiesState, commentsState: PostsCommentsState, postIds: number[], commentsLoadingState: LoadingState) => {
        const imageEntities = entities[ImageSchema.key];
        if (!imageEntities) {
            return {};
        }
        return postIds.reduce((acc, imageId) => {
            const key = `image/${imageId}`;
            const commentIds = getCommentIdsOrNull(commentsState, key);

            acc[imageId] = (commentIds !== null && commentsLoadingState !== LoadingState.LOADING)
                ? getComplaintsComplaintsCounts(entities, 'photoblog-comment', commentIds)
                : (imageEntities[imageId]
                    ? (imageEntities[imageId] as ImageEntity).complaintsCount  // note: not a fully denormalized entity
                    : 0
            );

            return acc;
        }, {});
    },
);

export const getModeratorUsersByGroupId = memoizeOne((moderators: GroupMemberEntity[]): UsersByGroupId => moderators.reduce((acc, moderator) => {
    if (!moderator.group) {
        return acc;
    }
    const groupId = moderator.group.id;
    if (!acc[groupId]) {
        acc[groupId] = [];
    }
    acc[groupId].push(moderator.user);
    return acc;
}, {}));
